import { Injectable, signal } from '@angular/core';

export type DimKey =
  | 'carrier'
  | 'amBest'
  | 'premiumType'
  | 'index'
  | 'crediting'
  | 'combo'
  | 'mktg'
  | 'premium'
  | 'surrYears'
  | 'faRateTerm'
  | 'faSurrYears'
  | 'faBonusYears'
  | 'bonus'
  | 'inverse'
  | 'mva'
  | 'rop'
  | 'riders'
  | 'states'
  | 'owner'
  | 'annuitant'
  | 'faMva'
  | 'faQualified';

/**
 * Service mediates rail filter-section navigation; was previously the right-panel
 * mediator pre-2026-05-30. The right-docked AG Grid sidebar tool panel has been
 * removed and all picker UI relocated INTO the left rail under a "Filters" section.
 *
 * Kept signals (still useful for the rail):
 *   - `focusDimension` — rail effect listens; expands the group + scrolls to the dim
 *   - `rememberFocusOrigin` / `lastFocusEl` — restore focus after a row-click triggers scroll
 *
 * Removed (panel-era state):
 *   - `panelVisible` / `_panelVisible` (no panel)
 *   - `setApiResolver` / `apiResolver` / `gridApi` getter (no openToolPanel call)
 *   - `openToolPanel` / `closeToolPanel` calls inside open-/close- methods (no AG Grid sidebar)
 *   - `toolPanelInitialized` (no lifecycle hook)
 *   - `focusSearch` / `openWithSearch` (2026-06-04 refinement #3: outer "Filter
 *      the filters" search input + Ctrl+F handler removed; nothing left to focus)
 */
@Injectable({ providedIn: 'root' })
export class RatesFilterPanelService {
  /** One-shot — read in an effect by the rail; expands group + scrolls to dim. */
  readonly focusDimension = signal<DimKey | null>(null);

  private lastFocusEl: HTMLElement | null = null;

  /** Save the element a row/CTA was clicked from so we can restore focus later. */
  rememberFocusOrigin(el: HTMLElement): void {
    this.lastFocusEl = el;
  }

  /**
   * Scroll the rail's Filters section to the matching dim.
   * Pre-2026-05-30 this called `gridApi.openToolPanel('filters')`; now it just
   * sets the one-shot signal that the rail's effect picks up.
   */
  openTo(dimOrTop: DimKey | 'top'): void {
    if (dimOrTop === 'top') {
      this.focusDimension.set(null);
    } else {
      this.focusDimension.set(dimOrTop);
    }
  }

  /** Restore focus to the last opener element saved by rememberFocusOrigin. */
  restoreFocus(returnFocusTo?: HTMLElement): void {
    const target = returnFocusTo ?? this.lastFocusEl;
    this.lastFocusEl = null;
    if (target?.isConnected) queueMicrotask(() => target.focus());
  }
}
